/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2026 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, { useState } from 'react'
import DefaultCrudTable from '@/components/tables/DefaultCrudTable'
import { columns } from './handlers'
import CustomTableBody from './components/CustomTableBody'
import {
  getListingBatchItemsByBatchIdAsCsv,
  retryListingBatch,
  retryListingBatchItem
} from '@/services/listing-batches'
import RetryModal from './components/RetryModal'
import { useAuth0 } from '@auth0/auth0-react'
import { Box, Button } from '@chakra-ui/react'
import MainTotalItems from '@/components/listings/MainTotalItems'
import DetailsModal from './components/DetailsModal'
import DownloadFileButton from '@/components/buttons/DownloadFileButton'
import { EXPORT_CSV } from '@/configs/strings/listings'
import { formatEmptyValue } from '@/utils/assets'
import { normalizeValueArrayObjectToString } from '@/utils/general'

const AssetsDataLists = ({
  batchHasErrors,
  batchId,
  batchIsProcessing,
  batchName,
  isHiddenForm,
  onChangePage,
  page,
  assets = [],
  reloadItems,
  totalPages,
  totalAssets
}) => {
  const [detailsModalIsOpen, setDetailsModalIsOpen] = useState(false)
  const [retryModalIsOpen, setRetryModalIsOpen] = useState(false)
  const [currentData, setCurrentData] = useState()
  const [retryBatch, setRetryBatch] = useState()
  const [retryProcess, setRetryProcess] = useState()
  const { getAccessTokenSilently: getAccessToken } = useAuth0()
  const currentAssets = assets.map(assetData => {
    const creators = formatEmptyValue(
      normalizeValueArrayObjectToString(assetData.asset.creators, 'name')
    )
    const publishers = formatEmptyValue(
      normalizeValueArrayObjectToString(assetData.asset.publishers, 'name')
    )
    return {
      ...assetData.asset,
      ...assetData,
      creators,
      publishers
    }
  })

  const onOpenModal = (assetData) => {
    setDetailsModalIsOpen(true)
    setCurrentData(assetData)
  }
  const onCloseModal = () => {
    setDetailsModalIsOpen(false)
    setCurrentData(null)
  }
  const onOpenRetryModal = (assetData) => {
    setRetryProcess(() => retryListingBatchItem)
    setCurrentData(assetData)
    setRetryModalIsOpen(true)
  }
  const onCloseRetryModal = () => {
    reloadItems()
    setRetryModalIsOpen(false)
    setCurrentData(null)
    setRetryBatch(false)
  }
  const onOpenRetryBatchModal = () => {
    setRetryProcess(() => retryListingBatch)
    setRetryModalIsOpen(true)
    setRetryBatch(true)
  }

  return (
    <>
      { detailsModalIsOpen &&
        <DetailsModal
          assetData={currentData}
          batchId={batchId}
          onClose={onCloseModal}
        />
      }
      { retryModalIsOpen &&
        <RetryModal
          batchId={batchId}
          batchName={batchName}
          getAccessToken={getAccessToken}
          onClose={onCloseRetryModal}
          onRetry={retryProcess}
          retryBatch={retryBatch}
          assetData={currentData}
        />
      }
      <section>
        <MainTotalItems
          label="Queued Assets for Listing"
          total={totalAssets}
        />
          <Box
            className="mt-4 mb-4"
            display="flex"
            justifyContent="space-between"
          >
            { batchHasErrors &&
              <Button onClick={onOpenRetryBatchModal}>Retry Batch</Button>
            }
            <DownloadFileButton
              service={
                (getAccessToken) =>
                  getListingBatchItemsByBatchIdAsCsv({ id: batchId }, getAccessToken)
              }
              isDisabled={!currentAssets?.length}
               dataKey="csv"
            >
              { EXPORT_CSV }
            </DownloadFileButton>
          </Box>
        { assets.length > 0 &&
          <DefaultCrudTable
            page={page}
            totalPages={totalPages}
            onPageChange={(page) => onChangePage(page)}
          >
            <DefaultCrudTable.Header columns={columns} />
            <CustomTableBody
              columns={columns}
              getAccessToken={getAccessToken}
              isHiddenForm={isHiddenForm}
              items={currentAssets}
              onDetails={onOpenModal}
              onRetry={!batchIsProcessing && onOpenRetryModal}
            />
          </DefaultCrudTable>
        }
      </section>
    </>
  )
}

export default AssetsDataLists
